import { useEffect, useRef, useState } from "react";
import { ArrowRight, X } from "lucide-react";
import { useLocation, useNavigate } from "react-router-dom";
import { useLang } from "@/lib/i18n";
import { startTour } from "@/lib/tour";
import BookingPartnerLogo from "@/components/yts/BookingPartnerLogo";

const DONE_KEY = "yts:welcome:done"; // Keep honoring visitors who completed the previous tour.
const COOKIE_KEY = "yts_welcome_seen";
const COOKIE_WINDOW = 60 * 60 * 24 * 60;

function hasSeenWelcome() {
  try {
    if (document.cookie.split(";").some((part) => part.trim() === `${COOKIE_KEY}=1`)) return true;
  } catch {
    // Private browsing may block cookies; local storage is the fallback.
  }
  try {
    return window.localStorage.getItem(DONE_KEY) === "1";
  } catch {
    return false;
  }
}

function rememberWelcome() {
  try {
    const secure = window.location.protocol === "https:" ? "; Secure" : "";
    document.cookie = `${COOKIE_KEY}=1; Max-Age=${COOKIE_WINDOW}; Path=/; SameSite=Lax${secure}`;
  } catch {
    // Continue without cookie support.
  }
  try {
    window.localStorage.setItem(DONE_KEY, "1");
  } catch {
    // The visitor can still take the tour if storage is disabled.
  }
}

/**
 * First visit only: a light language welcome followed by the actual site tour.
 * The tour is not forced onto visitors arriving through a deep link; it is
 * offered when they reach the landing page. Returning visitors see the site.
 */
export default function WelcomeTour() {
  const { lang, chooseLang } = useLang();
  const location = useLocation();
  const navigate = useNavigate();
  const [open, setOpen] = useState(() => !hasSeenWelcome());
  const launchTimer = useRef(null);
  const es = lang === "es";
  const onLanding = location.pathname === "/" || location.pathname === "/es";

  useEffect(() => () => window.clearTimeout(launchTimer.current), []);

  const begin = (chosen) => {
    chooseLang(chosen);
    rememberWelcome();
    setOpen(false);
    // Let the welcome dialog unmount before the site-tour card appears.
    launchTimer.current = window.setTimeout(() => startTour(), 100);
  };

  const skip = () => {
    chooseLang(lang);
    rememberWelcome();
    setOpen(false);
    navigate("/", { replace: true });
  };

  if (!open || !onLanding) return null;

  return (
    <div
      className="fixed inset-0 z-[1200] flex items-center justify-center bg-[#0c0b0a]/75 px-4"
      role="dialog"
      aria-modal="true"
      aria-label={es ? "Guía de bienvenida" : "Welcome tour"}
    >
      <div className="w-full max-w-[400px] rounded-2xl border border-black/[0.08] bg-white p-5 shadow-[0_26px_70px_-24px_rgba(0,0,0,0.65)] dark:border-white/[0.14] dark:bg-[#1c1a17]">
        <div className="flex items-start justify-between gap-3">
          <p className="text-[10px] font-extrabold uppercase tracking-[0.15em] text-[#c62e2a]">
            YA TÚ SABES PR · {es ? "BIENVENIDO" : "WELCOME"}
          </p>
          <button
            type="button"
            onClick={skip}
            aria-label={es ? "Omitir la guía" : "Skip the tour"}
            className="-mr-1 -mt-1 grid h-11 w-11 shrink-0 place-items-center rounded-lg border border-black/20 text-[#171719] transition hover:bg-black/[0.05] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#c62e2a] dark:border-white/25 dark:text-white dark:hover:bg-white/10"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
        <h2 className="mt-2 text-[21px] font-extrabold leading-tight tracking-[-0.03em] text-[#171719] dark:text-[#f5f3ef]">
          Elige tu idioma / Choose your language
        </h2>
        <p className="mt-2 text-[13.5px] leading-[1.55] text-[#5f5e57] dark:text-[#c9c4bb]">
          {es
            ? "Explora los barrios, el mapa y experiencias reservables. Viator y FareHarbor te conectan con las opciones de reserva. Avanza a tu ritmo o cierra el recorrido con la X."
            : "Explore neighborhoods, the map and bookable experiences. Viator and FareHarbor show you where to book. Advance at your own pace or close the tour anytime."}
        </p>
        <div className="mt-3 flex items-center gap-2" aria-label={es ? "Plataformas de reservas" : "Booking platforms"}>
          <span className="flex min-h-8 items-center rounded-[3px] border border-black/10 bg-white px-2"><BookingPartnerLogo provider="viator" className="!h-[16px]" /></span>
          <span className="flex min-h-8 items-center rounded-[3px] border border-black/10 bg-white px-2"><BookingPartnerLogo provider="fareharbor" className="!h-[16px]" /></span>
        </div>
        <div className="mt-4 grid grid-cols-2 gap-2">
          <button
            type="button"
            onClick={() => begin("es")}
            className="flex h-12 items-center justify-center gap-2 rounded-xl border border-[#171719] bg-[#171719] text-[14px] font-bold text-white transition hover:bg-black focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#c62e2a] dark:border-white dark:bg-white dark:text-[#171614]"
          >
            <span aria-hidden="true" className="text-[16px]">🇵🇷</span>
            Español <ArrowRight className="h-3.5 w-3.5" />
          </button>
          <button
            type="button"
            onClick={() => begin("en")}
            className="flex h-12 items-center justify-center gap-2 rounded-xl border border-black/[0.14] bg-white text-[14px] font-bold text-[#171719] transition hover:border-black/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#c62e2a] dark:border-white/20 dark:bg-white/[0.06] dark:text-white"
          >
            <span aria-hidden="true" className="text-[16px]">🇺🇸</span>
            English <ArrowRight className="h-3.5 w-3.5" />
          </button>
        </div>
        <button
          type="button"
          onClick={skip}
          className="mt-3 min-h-10 w-full text-center text-[12px] font-semibold text-[#76746e] transition hover:text-[#171719] dark:text-white/60 dark:hover:text-white"
        >
          {es ? "Omitir recorrido y explorar" : "Skip tour and explore"}
        </button>
        <p className="mt-1 text-center text-[10px] text-[#8d8d88] dark:text-white/40">
          {es ? "Solo se muestra una vez. Puedes repetirlo desde el menú." : "Shown once. Replay anytime from the menu."}
        </p>
      </div>
    </div>
  );
}
